import React from "react";

/**
 * Calculates the final price based on the current pricing model
 * @param {string} model - Current pricing model: "baseMultiplier", "directValues", or "hybrid"
 * @param {string} locality - Locality type: "metro", "urban", "suburban", "rural"
 * @param {string} tier - Demand tier: "high", "standard", "low"
 * @param {string} priceType - Price type: "joiningFee", "proMonthly", "premiumMonthly", "featuredWeekly"
 * @param {object} baseFeesModel - Base fees for each locality
 * @param {object} demandMultipliers - Multipliers for each demand tier
 * @param {object} directValues - Direct price matrix (for directValues model)
 * @param {array} overrides - Price overrides (for hybrid model)
 * @returns {number} Calculated price
 */
export const calculatePrice = (
  model,
  locality,
  tier,
  priceType,
  baseFeesModel,
  demandMultipliers,
  directValues,
  overrides
) => {
  // Check for override first (hybrid model)
  if (model === "hybrid" && overrides && overrides.length > 0) {
    const override = overrides.find(
      (o) => o.locality === locality && o.tier === tier && o.priceType === priceType
    );
    if (override) {
      return override.price;
    }
  }

  if (model === "baseMultiplier") {
    // Base Fee × Multiplier
    const baseFee = baseFeesModel[locality] || 0;
    const multiplier = demandMultipliers[tier] || 1.0;
    return Math.round(baseFee * multiplier);
  }

  if (model === "directValues") {
    // Direct lookup from matrix
    if (directValues[priceType] && directValues[priceType][locality]) {
      return directValues[priceType][locality][tier] || 0;
    }
    return 0;
  }

  if (model === "hybrid") {
    // Base Fee × Multiplier (same as baseMultiplier, but with override capability)
    const baseFee = baseFeesModel[locality] || 0;
    const multiplier = demandMultipliers[tier] || 1.0;
    return Math.round(baseFee * multiplier);
  }

  return 0;
};

/**
 * Generates a preview matrix of calculated prices for a given priceType
 * @param {string} model - Current pricing model
 * @param {string} priceType - Price type: "joiningFee", "proMonthly", "premiumMonthly", "featuredWeekly"
 * @param {object} baseFeesModel - Base fees for each locality
 * @param {object} demandMultipliers - Multipliers for each demand tier
 * @param {object} directValues - Direct price matrix
 * @param {array} overrides - Price overrides
 * @returns {object} Matrix with localities as keys and {high, standard, low} as values
 */
export const generatePriceMatrix = (
  model,
  priceType,
  baseFeesModel,
  demandMultipliers,
  directValues,
  overrides
) => {
  const localities = ["metro", "urban", "suburban", "rural"];
  const tiers = ["high", "standard", "low"];
  const matrix = {};

  localities.forEach((locality) => {
    matrix[locality] = {};
    tiers.forEach((tier) => {
      matrix[locality][tier] = calculatePrice(
        model,
        locality,
        tier,
        priceType,
        baseFeesModel,
        demandMultipliers,
        directValues,
        overrides
      );
    });
  });

  return matrix;
};

/**
 * React component to display price preview matrix
 */
export const PricingPreviewTable = ({
  model,
  priceType,
  baseFeesModel,
  demandMultipliers,
  directValues,
  overrides,
  className = "",
}) => {
  const matrix = generatePriceMatrix(
    model,
    priceType,
    baseFeesModel,
    demandMultipliers,
    directValues,
    overrides
  );

  const localities = [
    { key: "metro", label: "Metro", color: "#ff4d4f" },
    { key: "urban", label: "Urban", color: "#1890ff" },
    { key: "suburban", label: "Suburban", color: "#722ed1" },
    { key: "rural", label: "Rural", color: "#52c41a" },
  ];

  return (
    <div className={`pricing-preview-table ${className}`}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
              Locality
            </th>
            <th className="border border-gray-300 px-4 py-2 text-center font-semibold">
              High (×1.5)
            </th>
            <th className="border border-gray-300 px-4 py-2 text-center font-semibold">
              Standard (×1.0)
            </th>
            <th className="border border-gray-300 px-4 py-2 text-center font-semibold">
              Low (×0.6)
            </th>
          </tr>
        </thead>
        <tbody>
          {localities.map((locality) => (
            <tr key={locality.key}>
              <td
                className="border border-gray-300 px-4 py-2 font-medium"
                style={{ borderLeftColor: locality.color, borderLeftWidth: "3px" }}
              >
                {locality.label}
              </td>
              <td className="border border-gray-300 px-4 py-2 text-center">
                ₹{matrix[locality.key]?.high || 0}
              </td>
              <td className="border border-gray-300 px-4 py-2 text-center">
                ₹{matrix[locality.key]?.standard || 0}
              </td>
              <td className="border border-gray-300 px-4 py-2 text-center">
                ₹{matrix[locality.key]?.low || 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default {
  calculatePrice,
  generatePriceMatrix,
  PricingPreviewTable,
};
